import { Text } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Stack,
  TextField,
  ThemeProvider,
} from "@mui/material";
import { RootPaths } from "@src/appV2/App/paths";
import { parseJwtPayload } from "@src/appV2/Auth/cognito/tokenUtils";
import { environmentConfig } from "@src/appV2/environment";
import { ClipboardLogo } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
// eslint-disable-next-line no-restricted-imports
import { getCognitoSingleton } from "@src/lib/cognito/src/lib/cognitoSingleton";
// eslint-disable-next-line no-restricted-imports
import { getFirebaseSingleton } from "@src/lib/firebase/src";
import { type ReactElement, useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { exchangeCognitoTokenForFirebase } from "../cognito/tokenExchange";
import { useImpersonation } from "./context";
import { postMessageToParent } from "./postMessageToParent";

type ImpersonateState =
  | "loading"
  | "waiting"
  | "manual-input"
  | "success"
  | "error"
  | "session-ended";

export function ImpersonatePage(): ReactElement {
  const [state, setState] = useState<ImpersonateState>("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const [usernameInput, setUsernameInput] = useState("");
  const [tokenInput, setTokenInput] = useState("");
  const history = useHistory();
  const location = useLocation();
  const { startImpersonation, endImpersonation } = useImpersonation();
  const hasStartedAuth = useRef(false);

  const searchParams = new URLSearchParams(location.search);
  const usernameFromUrl = searchParams.get("username") ?? "";

  const isInIframe = window.parent !== window;

  const authenticate = useCallback(
    async (username: string, employeeIdToken: string) => {
      setState("loading");

      try {
        logEvent(APP_V2_APP_EVENTS.IMPERSONATION_TOKEN_RECEIVED);

        const cognito = getCognitoSingleton();
        const credentials = await cognito.signInAsImpersonator(username, employeeIdToken);

        logEvent(APP_V2_APP_EVENTS.IMPERSONATION_AUTH_SUCCESS);

        const firebaseToken = await exchangeCognitoTokenForFirebase();

        const accessTokenPayload = credentials.accessToken
          ? parseJwtPayload(credentials.accessToken)
          : {};
        const impersonatedBy = (accessTokenPayload.impersonated_by as string) ?? "admin";

        startImpersonation({
          impersonatedBy,
          cognitoUsername: username,
          accessToken: credentials.accessToken ?? "",
        });

        await getFirebaseSingleton().signInWithCustomToken(firebaseToken);

        setState("success");
        postMessageToParent({ type: "impersonation:success" });

        history.replace(RootPaths.APP_V2_HOME);
      } catch (error) {
        endImpersonation();

        logError(APP_V2_APP_EVENTS.IMPERSONATION_AUTH_FAILURE, {
          error,
          metadata: { cognitoUsername: username },
        });

        const errorWithMessage = error as { message?: string };
        const message = errorWithMessage.message ?? "Impersonation authentication failed.";
        setErrorMessage(message);
        setState("error");
        postMessageToParent({ type: "impersonation:error", error: message });
      }
    },
    [history, startImpersonation, endImpersonation]
  );

  useEffect(() => {
    if (hasStartedAuth.current) {
      return;
    }

    logEvent(APP_V2_APP_EVENTS.IMPERSONATION_STARTED, {
      isInIframe,
      hasCognitoUsername: Boolean(usernameFromUrl),
    });

    if (isInIframe) {
      if (!usernameFromUrl) {
        setErrorMessage(
          "Missing username parameter. The admin app must provide a username in the URL."
        );
        setState("error");
        postMessageToParent({
          type: "impersonation:error",
          error: "Missing username query parameter",
        });
        return;
      }

      setState("waiting");

      const allowedOrigin = environmentConfig.REACT_APP_ADMIN_WEB_APP_URL;

      const normalizedOrigin = allowedOrigin ? new URL(allowedOrigin).origin : "";

      function handleMessage(event: MessageEvent) {
        if (hasStartedAuth.current) {
          return;
        }

        if (!normalizedOrigin || event.origin !== normalizedOrigin) {
          return;
        }

        const data = event.data as { type?: string; token?: string } | undefined;
        if (data?.type === "employeeIdToken" && data.token) {
          hasStartedAuth.current = true;
          void authenticate(usernameFromUrl, data.token);
        }
      }

      window.addEventListener("message", handleMessage);
      return () => {
        window.removeEventListener("message", handleMessage);
      };
    }

    setState("manual-input");
    return undefined;
  }, [isInIframe, usernameFromUrl, authenticate]);

  return (
    <ThemeProvider theme={getTheme()}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          paddingY: 4,
          paddingX: 6,
          backgroundColor: (theme) => theme.palette.background.default,
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Container maxWidth="sm">
            <Stack spacing={4} alignItems="center" textAlign="center">
              <ClipboardLogo width={200} />

              {(state === "loading" || state === "waiting") && (
                <>
                  <CircularProgress />
                  <Text variant="h5">
                    {state === "waiting"
                      ? "Waiting for employee token..."
                      : "Authenticating as impersonator..."}
                  </Text>
                </>
              )}

              {state === "manual-input" && (
                <>
                  <Text variant="h5">Impersonation</Text>
                  {usernameFromUrl ? (
                    <Text>
                      Paste the employee ID token below to impersonate worker{" "}
                      <strong>{usernameFromUrl}</strong>.
                    </Text>
                  ) : (
                    <>
                      <Text>
                        Enter the Cognito username and employee ID token to start impersonation.
                      </Text>
                      <TextField
                        fullWidth
                        label="Cognito Username"
                        value={usernameInput}
                        onChange={(event) => {
                          setUsernameInput(event.target.value);
                        }}
                      />
                    </>
                  )}
                  <TextField
                    fullWidth
                    multiline
                    label="Employee ID Token"
                    minRows={3}
                    value={tokenInput}
                    onChange={(event) => {
                      setTokenInput(event.target.value);
                    }}
                  />
                  <Button
                    variant="contained"
                    size="large"
                    disabled={!tokenInput.trim() || (!usernameFromUrl && !usernameInput.trim())}
                    onClick={() => {
                      hasStartedAuth.current = true;
                      const username = usernameFromUrl || usernameInput.trim();
                      void authenticate(username, tokenInput.trim());
                    }}
                  >
                    Impersonate
                  </Button>
                </>
              )}

              {state === "success" && <Text variant="h5">Success! Redirecting...</Text>}

              {state === "error" && (
                <>
                  <Text variant="h5" color="error">
                    Impersonation Failed
                  </Text>
                  <Text>{errorMessage}</Text>
                  <Button
                    variant="contained"
                    sx={{ marginTop: 2 }}
                    onClick={() => {
                      setState("manual-input");
                    }}
                  >
                    Try Again
                  </Button>
                </>
              )}

              {state === "session-ended" && (
                <Text variant="h5">Impersonation session has ended.</Text>
              )}
            </Stack>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
